import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Brain, Search, TrendingUp, Target, Zap, Settings2,
  RefreshCw, CheckCircle2, Loader2, ChevronRight, Eye,
  Phone, Mail, MapPin, Briefcase, BarChart3, Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SearchTuningReport() {
  const [analytics, setAnalytics] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Editable fields
  const [editTitles, setEditTitles] = useState('');
  const [editKeyword, setEditKeyword] = useState('');
  const [editLocations, setEditLocations] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [analyticsRes, paramsRes] = await Promise.all([
        api.get(`${API}/api/leads/discover/analytics`),
        api.get(`${API}/api/brain/icp/search-params`),
      ]);
      setAnalytics(analyticsRes.data);
      const params = paramsRes.data.search_params;
      setSearchParams(params);
      setEditTitles((params?.titles || []).join(', '));
      setEditKeyword(params?.keyword || '');
      setEditLocations((params?.locations || []).join(', '));
    } catch (e) {
      console.error('Failed to load search tuning data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const titles = editTitles.split(',').map(t => t.trim()).filter(Boolean);
      const locations = editLocations.split(',').map(l => l.trim()).filter(Boolean);
      const res = await api.post(`${API}/api/brain/icp/search-preview`, {
        titles,
        keyword: editKeyword.trim(),
        locations,
      });
      setPreview(res.data.preview);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const titles = editTitles.split(',').map(t => t.trim()).filter(Boolean);
      const locations = editLocations.split(',').map(l => l.trim()).filter(Boolean);
      await api.post(`${API}/api/brain/icp/refine-search`, {
        titles,
        keyword: editKeyword.trim(),
        locations,
      });
      toast.success('Search parameters saved');
      setEditMode(false);
      loadData();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const stats = analytics?.analytics || {};
  const margin = analytics?.margin_info || {};

  const statusConfig = {
    no_data: { label: 'No Data', color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
    collecting_data: { label: 'Collecting', color: 'text-amber-400', bg: 'bg-amber-500/10' },
    learning: { label: 'Learning', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    optimized: { label: 'Optimized', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  };
  const status = statusConfig[stats.optimization_status] || statusConfig.no_data;

  if (loading) {
    return (
      <div className="space-y-6 p-1" data-testid="search-tuning-loading">
        <div className="flex items-center gap-3">
          <Brain className="w-7 h-7 text-cyan-400" />
          <h1 className="text-2xl font-bold tracking-tight">Smart Search Tuning</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}><CardContent className="p-6"><div className="h-16 bg-muted animate-pulse rounded" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1" data-testid="search-tuning-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-violet-500/20">
            <Brain className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Smart Search Tuning</h1>
            <p className="text-sm text-muted-foreground">Brain-powered search optimization for Lockene lead discovery</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-analytics">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Brain Status</p>
                <div className={`mt-1 text-lg font-bold ${status.color}`}>{status.label}</div>
              </div>
              <div className={`p-2.5 rounded-lg ${status.bg}`}>
                <Sparkles className={`w-5 h-5 ${status.color}`} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {stats.total_searches || 0} searches analyzed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Actionable Rate</p>
                <div className="mt-1 text-lg font-bold text-emerald-400">
                  {stats.avg_actionable_rate ? `${(stats.avg_actionable_rate * 100).toFixed(0)}%` : '—'}
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-500/10">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Leads with email + phone
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Avg Leads/Search</p>
                <div className="mt-1 text-lg font-bold">
                  {stats.avg_actionable ? Math.round(stats.avg_actionable) : '—'}
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-cyan-500/10">
                <Target className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Quality leads per discovery
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Cost/Reveal</p>
                <div className="mt-1 text-lg font-bold text-amber-400">
                  ${margin.cost_per_reveal || '0.065'}
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <Zap className="w-5 h-5 text-amber-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {margin.lockene_margin || '30%'} margin included
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search Parameters Card */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-cyan-400" />
                Lockene Search Parameters
              </CardTitle>
              <Button
                variant={editMode ? "default" : "outline"}
                size="sm"
                onClick={() => setEditMode(!editMode)}
                data-testid="toggle-edit-mode"
              >
                {editMode ? 'Cancel' : 'Refine'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {editMode ? (
              <>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Target Titles (comma-separated)</label>
                  <input
                    className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                    value={editTitles}
                    onChange={e => setEditTitles(e.target.value)}
                    placeholder="CTO, CEO, VP Engineering"
                    data-testid="edit-titles-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Industry Keyword</label>
                  <input
                    className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                    value={editKeyword}
                    onChange={e => setEditKeyword(e.target.value)}
                    placeholder="Technology, Finance, Healthcare"
                    data-testid="edit-keyword-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Locations (comma-separated)</label>
                  <input
                    className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                    value={editLocations}
                    onChange={e => setEditLocations(e.target.value)}
                    placeholder="India, United States, United Kingdom"
                    data-testid="edit-locations-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handlePreview} disabled={previewing} data-testid="preview-search-btn">
                    {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
                    Preview
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-search-params-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Briefcase className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Titles</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(searchParams?.titles || []).map((t, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Search className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Keyword</p>
                    <span className="text-sm font-medium">{searchParams?.keyword || '—'}</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Locations</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(searchParams?.locations || []).map((l, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 text-xs font-medium">{l}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {searchParams?.manually_refined && (
                  <p className="text-xs text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Manually refined
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Results / Performance */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-400" />
              {preview ? 'Search Preview' : 'Performance Summary'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold">{preview.total_returned}</div>
                    <div className="text-xs text-muted-foreground">Total Found</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-emerald-500/10">
                    <div className="text-2xl font-bold text-emerald-400">{preview.actionable}</div>
                    <div className="text-xs text-muted-foreground">Actionable</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-cyan-500/10">
                    <div className="text-2xl font-bold text-cyan-400">{(preview.actionable_rate * 100).toFixed(0)}%</div>
                    <div className="text-xs text-muted-foreground">Rate</div>
                  </div>
                </div>
                {preview.sample_contacts?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Sample Contacts</p>
                    {preview.sample_contacts.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-md bg-muted/30 text-sm">
                        <div>
                          <span className="font-medium">{c.name || 'Unknown'}</span>
                          <span className="text-muted-foreground ml-1">· {c.title}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {c.has_email && <Mail className="w-3.5 h-3.5 text-emerald-400" />}
                          {c.has_phone && <Phone className="w-3.5 h-3.5 text-cyan-400" />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => setPreview(null)} className="w-full">
                  Back to Summary
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Total Searches</span>
                  <span className="text-sm font-medium">{stats.total_searches || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Avg Returned</span>
                  <span className="text-sm font-medium">{stats.avg_returned ? Math.round(stats.avg_returned) : '—'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Avg Actionable</span>
                  <span className="text-sm font-medium text-emerald-400">{stats.avg_actionable ? Math.round(stats.avg_actionable) : '—'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Best Rate</span>
                  <span className="text-sm font-medium text-cyan-400">{stats.best_rate ? `${(stats.best_rate * 100).toFixed(0)}%` : '—'}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Total Imported</span>
                  <span className="text-sm font-medium">{stats.total_imported || 0}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Best Search Params */}
      {stats.best_search_params && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              Best Performing Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Titles</p>
                <div className="flex flex-wrap gap-1">
                  {(stats.best_search_params.titles || []).map((t, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs">{t}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Keyword</p>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs">
                  {(stats.best_search_params.keywords || [])[0] || '—'}
                </span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Locations</p>
                <div className="flex flex-wrap gap-1">
                  {(stats.best_search_params.locations || []).map((l, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs">{l}</span>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
